import React, { useEffect, useState } from "react";
import { AdminFees } from "@/components/AdminFees";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Check, X, ExternalLink } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("pending");
  const [applications, setApplications] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [msg, setMsg] = useState("");

  const load = async () => {
    if (tab === "fees") return;
    if (tab === "payouts") {
      const { data } = await api.get("/admin/payouts");
      setPayouts(data);
    } else if (tab === "requests") {
      const { data } = await api.get("/admin/payout-requests");
      setPayoutRequests(data);
    } else if (tab === "reviews") {
      const { data } = await api.get("/admin/reviews");
      setReviews(data);
    } else {
      const { data } = await api.get(`/admin/applications?status=${tab}`);
      setApplications(data);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [tab, user]);

  if (loading) return <div className="py-24 text-center text-[#A89896]">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const approve = async (id) => {
    try {
      await api.post(`/admin/applications/${id}/approve`);
      setMsg("Application approved. New practitioner is now live.");
      load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional):") || "";
    const fd = new FormData();
    fd.append("reason", reason);
    try {
      await api.post(`/admin/applications/${id}/reject`, fd);
      setMsg("Application rejected.");
      load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  const token = localStorage.getItem("tt_token");

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#E58B5E]">Admin</div>
      <h1 className="font-serif text-5xl mt-4 text-[#F7ECEB]">Touche Command</h1>

      <div className="mt-10 flex flex-wrap gap-2">
        {["pending", "approved", "rejected", "payouts", "requests", "reviews", "fees"].map((k) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-5 py-2 rounded-full text-sm border capitalize transition-colors ${
              tab === k ? "bg-[#C93B3B] text-white border-[#C93B3B]" : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A]"
            }`}>{k === "requests" ? "Payout Requests" : k === "fees" ? "Fees & Revenue" : k}</button>
        ))}
      </div>

      {msg && <div className="mt-4 text-sm text-[#C93B3B]" data-testid="admin-msg">{msg}</div>}

      {(tab === "pending" || tab === "approved" || tab === "rejected") && (
        <div className="mt-8 space-y-4">
          {applications.length === 0 && <div className="text-[#A89896] py-12 text-center">No {tab} applications.</div>}
          {applications.map((a) => (
            <div key={a.id} className="tt-card p-6" data-testid={`app-${a.id}`}>
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  {a.photo_path ? (
                    <div className="aspect-[4/5] rounded-lg overflow-hidden">
                      <img src={`${API}/files/${a.photo_path}?auth=${encodeURIComponent(token || "")}`} alt={a.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="aspect-[4/5] rounded-lg bg-[#110D0D]" />
                  )}
                </div>
                <div className="md:col-span-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-serif text-2xl text-[#F7ECEB]">{a.name}</h3>
                      <div className="text-sm text-[#C93B3B]">{a.location} · {a.years_experience} yrs · ${a.hourly_rate}/hr</div>
                      <div className="text-xs text-[#A89896] mt-1">{a.email}</div>
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#A89896]">{a.status}</div>
                  </div>
                  <p className="text-[#F7ECEB] mt-4 leading-relaxed">{a.bio}</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(a.specialties || []).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-[#110D0D] text-[#C93B3B]">{s}</span>
                    ))}
                  </div>

                  {a.verification_paths?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs uppercase tracking-[0.2em] text-[#E58B5E]">Verification files ({a.verification_paths.length})</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {a.verification_paths.map((p, i) => (
                          <a key={i} href={`${API}/files/${p}?auth=${encodeURIComponent(token || "")}`} target="_blank" rel="noreferrer"
                            data-testid={`verif-${a.id}-${i}`}
                            className="text-xs px-3 py-1.5 rounded-full border border-[#382A2A] text-[#C93B3B] flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Doc {i + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {tab === "pending" && (
                    <div className="mt-6 flex gap-3">
                      <button onClick={() => approve(a.id)} className="btn-primary text-sm" data-testid={`approve-${a.id}`}>
                        <Check className="w-4 h-4 inline mr-1" /> Approve
                      </button>
                      <button onClick={() => reject(a.id)} className="btn-ghost text-sm" data-testid={`reject-${a.id}`}>
                        <X className="w-4 h-4 inline mr-1" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "fees" && <AdminFees />}

      {tab === "payouts" && (
        <div className="mt-8 tt-card p-6">
          <div className="grid grid-cols-5 gap-4 text-xs uppercase tracking-[0.2em] text-[#C93B3B] pb-3 border-b border-[#382A2A]">
            <div className="col-span-2">Practitioner</div>
            <div>Sessions</div>
            <div>Gross</div>
            <div>Cuddler share (85%)</div>
          </div>
          {payouts.length === 0 && <div className="text-[#A89896] py-8 text-center">No confirmed bookings yet.</div>}
          {payouts.map((p) => (
            <div key={p.practitioner_id} className="grid grid-cols-5 gap-4 py-3 border-b border-[#382A2A] text-sm items-center" data-testid={`payout-${p.practitioner_id}`}>
              <div className="col-span-2 font-medium text-[#F7ECEB]">{p.practitioner_name}</div>
              <div className="text-[#A89896]">{p.session_count}</div>
              <div className="font-serif text-lg text-[#F7ECEB]">${p.gross.toFixed(0)}</div>
              <div className="font-serif text-lg text-[#C93B3B]">${p.cuddler_share.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "requests" && (
        <div className="mt-8 space-y-3" data-testid="admin-requests-tab">
          {payoutRequests.length === 0 && <div className="text-[#A89896] py-8 text-center">No payout requests.</div>}
          {payoutRequests.map((r) => (
            <div key={r.id} className="tt-card p-6" data-testid={`req-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-2xl text-[#F7ECEB]">${r.amount.toFixed(2)}</div>
                  <div className="text-sm text-[#A89896] mt-1">{r.practitioner_name || r.email} · {r.method_preference}</div>
                  <div className="text-xs text-[#A89896] mt-1">{r.created_at?.slice(0, 10)}</div>
                  {r.payout_details && <div className="text-xs text-[#A89896] mt-2 italic">"{r.payout_details}"</div>}
                </div>
                <div className="text-right">
                  <div className={`text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full inline-block ${
                    r.status === "paid" ? "bg-[#C93B3B] text-white" : "bg-[#110D0D] text-[#A89896]"
                  }`}>{r.status}</div>
                  {r.status === "pending" && (
                    <button onClick={async () => {
                      const note = window.prompt("Payment note (optional):") || "";
                      const fd = new FormData(); fd.append("method", r.method_preference); fd.append("note", note);
                      await api.post(`/admin/payout-requests/${r.id}/mark-paid`, fd);
                      setMsg("Marked as paid.");
                      load();
                    }} data-testid={`markpaid-${r.id}`} className="btn-primary text-xs mt-3 block">Mark as paid</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "reviews" && (
        <div className="mt-8 space-y-3">
          {reviews.length === 0 && <div className="text-[#A89896] py-8 text-center">No reviews yet.</div>}
          {reviews.map((r) => (
            <div key={r.id} className="tt-card p-6" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-[#F7ECEB]">{r.author_name} → {r.practitioner_name}</div>
                <div className="text-[#E58B5E]">{"★".repeat(r.rating)}<span className="text-[#382A2A]">{"★".repeat(5 - r.rating)}</span></div>
              </div>
              <p className="text-[#F7ECEB] mt-3">{r.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
